import {defineStore} from "pinia";
import {DefaultOrganizationSettings, type OrganizationSettings} from "@/models/organization/organization-settings";
import {DefaultOrganizationLoadUnloadNotificationSettings} from "@/models/organization/organization-load-unload-notification-settings";
import { DefaultRateGroupModel, type RateGroup } from "@/models/rate-group/rate-group";

import {
    getFeatureToggles,
    getOrganizationSettings,
    updateOrganizationSettings,
    getOrganizationPriceSettings,
    getOrganizationCountry,
    getOrganizationDataExeSettings,
    updateOrganizationDataExeSettings,
    getBranchMappings,
    saveBranchMappings,
    getGroupMappings,
    saveGroupMappings
} from "@/services/organization/organization-service";
import type { BranchMappingGroup, BranchMappingListItem, GroupMappingGroup, GroupMappingListItem } from "@/models/organization/branch-mapping";
import type {FeatureToggle} from "@/constants/feature-toggle";
import {type RemovableRef, useStorage} from "@vueuse/core";
import {addCacheItem, type BrowserCache, clearExpiredItems, getCacheItem} from "@/helpers/cache/cache";
import { Country } from "@/constants/country";
import type { OrganizationDataExeSettings } from "@/models/organization/organization-data-exe-settings";
import { TimeSelectionMode } from '@/constants/time-selection-mode';
import { DefaultPickUpDropOffTimeRange } from '@/constants/assignment';
import { DefaultTransportStartTime } from '@/constants/default-date-settings';

const lifetime = 14_400_000; // 4 hours

interface organizationState {
    organizationSettings: OrganizationSettings;
    rateGroup: RateGroup;
    loading: boolean;
    editMode: boolean;
    featureToggles: FeatureToggle[];
    featureTogglesCache: RemovableRef<BrowserCache<FeatureToggle[]>[]>;
    organizationCountry: Country;
    dataExeSettings: OrganizationDataExeSettings;
    branchMappings: BranchMappingGroup[];
    branchMappingRelationNames: Record<number, string>;
    groupMappings: GroupMappingGroup[];
    settingsLoadedForOrganizationId: number;
    settingsPromise?: Promise<void>;
}

export const useOrganizationStore = defineStore({
    id: 'settings',
    state: () => ({
        organizationSettings: DefaultOrganizationSettings(),
        rateGroup: DefaultRateGroupModel(),
        editMode: false,
        loading: false,
        organizationCountry: Country.NL,
        featureToggles: [],
        featureTogglesCache: useStorage("enabled-flags", []),
        dataExeSettings: {
            dataExeId: 0,
            dataExeName: null
        },
        branchMappings: [],
        branchMappingRelationNames: {},
        groupMappings: [],
        settingsLoadedForOrganizationId: 0,
        settingsPromise: undefined
    } as organizationState),
    actions: {
        async getFeatureToggles(forceRefresh = false) {
            const key = "enabled-flags";
            this.featureTogglesCache = clearExpiredItems(this.featureTogglesCache, lifetime);

            if (!forceRefresh) {
                const cachedData = getCacheItem(this.featureTogglesCache, key);
                if (cachedData && cachedData.length > 0) {
                    this.featureToggles = cachedData;
                    return;
                }
            }

            const response = await getFeatureToggles()
            if (response.status === 200) {
                this.featureToggles = response.data;
                this.featureTogglesCache = addCacheItem(this.featureTogglesCache, key, this.featureToggles);
            }

            return response.status === 200;
        },
        /**
         * Read-only consumers (modals/drawers that only need defaults) should use
         * this instead of getOrganizationSettings: it fetches at most once per
         * organization and dedupes concurrent callers.
         */
        async ensureOrganizationSettings(id: number) {
            if (!id || this.settingsLoadedForOrganizationId === id) return;

            if (!this.settingsPromise) {
                this.settingsPromise = this.getOrganizationSettings(id).finally(() => {
                    this.settingsPromise = undefined;
                });
            }

            await this.settingsPromise;
        },
        async getOrganizationSettings(id: number) {
            this.loading = true;
            const response = await getOrganizationSettings(id)
            if (response.status === 200) {
                this.settingsLoadedForOrganizationId = id;
                this.organizationSettings = response.data;

                if (this.organizationSettings.usePtvRoutingFallback == null) {
                    this.organizationSettings.usePtvRoutingFallback = false;
                }

                if (this.organizationSettings.useTransportVehicleStationForTransport == null) {
                    this.organizationSettings.useTransportVehicleStationForTransport = false;
                }

                if (!this.organizationSettings.dateSettings) {
                    this.organizationSettings.dateSettings = {
                        id: 0,
                        defaultStartDate: 1,
                        defaultDateRange: 1,
                        timeSelectionMode: TimeSelectionMode.DaysAndTime,
                        defaultStartTime: DefaultPickUpDropOffTimeRange[0],
                        defaultEndTime: DefaultPickUpDropOffTimeRange[1],
                        defaultTransportStartTime: DefaultTransportStartTime,
                        allowSeparateLoadUnloadWindows: false,
                        windowInputMode: null
                    }
                }

                if (!this.organizationSettings.loadUnloadNotificationSettings) {
                    this.organizationSettings.loadUnloadNotificationSettings = DefaultOrganizationLoadUnloadNotificationSettings();
                }
            }
            this.loading = false;
        },
        async updateOrganizationSettings() {
            this.loading = true;

            const response = await updateOrganizationSettings(this.organizationSettings)

            if (response.status == 200) {
                this.organizationSettings = response.data;
            }

            this.loading = false;

            return response;
        },
        async getOrganizationPriceSettings(id: number) {
            this.loading = true;
            const response = await getOrganizationPriceSettings(id)
            if (response.status === 200) {
                this.organizationSettings.priceSettings = response.data;
            }
            this.loading = false;
        },
        hasFeature(feature: FeatureToggle) {
            return this.featureToggles.includes(feature);
        },
        async getCurrentOrganizationCountry(id: number) {
            this.loading = true;
            const response = await getOrganizationCountry(id);
            if (response.status == 200) {
                this.organizationCountry = response.data.country;
            }
            this.loading = false;
        },
        async getOrganizationDataExeSettings(id: number) {
            this.loading = true;
            const response = await getOrganizationDataExeSettings(id);
            if (response.status == 200) {
                this.dataExeSettings = response.data;
            }
            this.loading = false;
        },
        async updateOrganizationDataExeSettings() {
            this.loading = true;
            const response = await updateOrganizationDataExeSettings(this.dataExeSettings.dataExeId, this.dataExeSettings.dataExeName);
            this.loading = false;

            return response;
        },
        async getBranchMappings() {
            this.loading = true;
            const response = await getBranchMappings();
            if (response.status === 200) {
                const items: BranchMappingListItem[] = response.data;

                const names: Record<number, string> = {};
                const grouped = new Map<string, number[]>();
                for (const item of items) {
                    names[item.relationId] = item.relationName;
                    const list = grouped.get(item.branchMappingsCode);
                    if (list) {
                        if (!list.includes(item.relationId)) list.push(item.relationId);
                    } else {
                        grouped.set(item.branchMappingsCode, [item.relationId]);
                    }
                }

                this.branchMappingRelationNames = names;
                this.branchMappings = Array.from(grouped.entries()).map(([code, relationIds]) => ({
                    code,
                    relationIds
                }));
            }
            this.loading = false;
        },
        async saveBranchMappings() {
            this.loading = true;
            const flat = this.branchMappings.flatMap(group =>
                group.relationIds.map(relationId => ({
                    branchMappingsCode: group.code,
                    relationId
                }))
            );
            const response = await saveBranchMappings(flat);
            this.loading = false;
            return response;
        },
        async getGroupMappings() {
            this.loading = true;
            const response = await getGroupMappings();
            if (response.status === 200) {
                const items: GroupMappingListItem[] = response.data;
                const grouped = new Map<string, { groupName: string; relationIds: number[] }>();
                for (const item of items) {
                    this.branchMappingRelationNames[item.relationId] = item.relationName;
                    const entry = grouped.get(item.azureAdGroupId);
                    if (entry) {
                        if (!entry.relationIds.includes(item.relationId)) entry.relationIds.push(item.relationId);
                    } else {
                        grouped.set(item.azureAdGroupId, { groupName: item.azureAdGroupName, relationIds: [item.relationId] });
                    }
                }
                this.groupMappings = Array.from(grouped.entries()).map(([groupId, { groupName, relationIds }]) => ({
                    groupId,
                    groupName,
                    relationIds
                }));
            }
            this.loading = false;
        },
        async saveGroupMappings() {
            this.loading = true;
            const flat = this.groupMappings.flatMap(group =>
                group.relationIds.map(relationId => ({
                    azureAdGroupId: group.groupId,
                    azureAdGroupName: group.groupName,
                    relationId
                }))
            );
            const response = await saveGroupMappings(flat);
            this.loading = false;
            return response;
        }
    }
})
